import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Input, InputGroup, InputGroupAddon, InputGroupText, Modal, ModalHeader, ModalBody } from 'reactstrap';
import Icon from '../../../common/containers/icon';
import SinglePicker from '../../../common/controls/singlePicker';
import FormFieldLabel from '../label';
import { displayValueOrDash, filterAuditTrailEntries, formatTime, getUniqueSortedValues, googleMapsUrl, groupEntriesByDay, normalizeAuditTrailEntries, stringifyRawValueForModal } from './auditTrailViewerHelpers';
const DATE_PRESET_OPTIONS = [{
  id: 'today',
  title: 'Today'
}, {
  id: '24h',
  title: 'Last 24 hours'
}, {
  id: '7d',
  title: 'Last 7 days'
}];
const Filter = _ref => {
  let label = _ref.label,
    filterAriaLabel = _ref.filterAriaLabel,
    _ref$children = _ref.children,
    children = _ref$children === void 0 ? null : _ref$children;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormFieldLabel, {
    value: label
  }), /*#__PURE__*/React.createElement("div", {
    "aria-label": filterAriaLabel
  }, children));
};
Filter.propTypes = {
  label: PropTypes.string.isRequired,
  filterAriaLabel: PropTypes.string.isRequired,
  children: PropTypes.node
};
const Group = _ref2 => {
  let title = _ref2.title,
    _ref2$right = _ref2.right,
    right = _ref2$right === void 0 ? null : _ref2$right,
    _ref2$children = _ref2.children,
    children = _ref2$children === void 0 ? null : _ref2$children;
  return /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__group"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__group-header"
  }, /*#__PURE__*/React.createElement("span", {
    className: "t-audit-trail__group-title"
  }, title), right !== null && right !== void 0 ? right : null), children);
};
Group.propTypes = {
  title: PropTypes.string.isRequired,
  right: PropTypes.node,
  children: PropTypes.node
};
const Label = _ref3 => {
  let _ref3$children = _ref3.children,
    children = _ref3$children === void 0 ? null : _ref3$children;
  return /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__detail-label"
  }, children);
};
Label.propTypes = {
  children: PropTypes.node
};
const Value = _ref4 => {
  let _ref4$children = _ref4.children,
    children = _ref4$children === void 0 ? null : _ref4$children,
    _ref4$className = _ref4.className,
    className = _ref4$className === void 0 ? '' : _ref4$className;
  return /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__detail-value ".concat(className).trim()
  }, children);
};
Value.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};
const EyeIconButton = _ref5 => {
  let onClick = _ref5.onClick,
    _ref5$label = _ref5.label,
    label = _ref5$label === void 0 ? 'View Data' : _ref5$label;
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: onClick,
    "aria-label": label,
    title: label,
    className: "t-audit-trail__icon-button"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "eye"
  }));
};
EyeIconButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string
};
const ViewDataModal = _ref6 => {
  let jsonString = _ref6.jsonString,
    onClose = _ref6.onClose;
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: true,
    toggle: onClose,
    className: "t-audit-trail__modal",
    labelledBy: "audit-trail-view-data-title",
    fade: false
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: onClose
  }, /*#__PURE__*/React.createElement("span", {
    id: "audit-trail-view-data-title"
  }, "View data")), /*#__PURE__*/React.createElement(ModalBody, {
    className: "t-audit-trail__modal-content"
  }, /*#__PURE__*/React.createElement("pre", {
    className: "t-audit-trail__modal-body"
  }, jsonString)));
};
ViewDataModal.propTypes = {
  jsonString: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};
const Detail = _ref7 => {
  let entry = _ref7.entry,
    _ref7$onViewData = _ref7.onViewData,
    onViewData = _ref7$onViewData === void 0 ? null : _ref7$onViewData;
  const created = entry.created ? entry.created.toLocaleString() : '—';
  const server = entry.serverTimestamp ? entry.serverTimestamp.toLocaleString() : '—';
  const hasGeo = Boolean(entry.latitude != null && entry.longitude != null);
  const hasRawValue = entry.rawValueForModal != null;
  const valueDisplay = displayValueOrDash(entry.displayValue);
  return /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__detail"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__detail-grid"
  }, /*#__PURE__*/React.createElement(Label, null, "Path"), /*#__PURE__*/React.createElement(Value, null, entry.breadcrumbs || '—'), /*#__PURE__*/React.createElement(Label, null, "Content Path"), /*#__PURE__*/React.createElement(Value, null, entry.contentPath || '—'), /*#__PURE__*/React.createElement(Label, null, "Created"), /*#__PURE__*/React.createElement(Value, null, created), /*#__PURE__*/React.createElement(Label, null, "Server Time"), /*#__PURE__*/React.createElement(Value, null, server), /*#__PURE__*/React.createElement(Label, null, "From Value"), /*#__PURE__*/React.createElement(Value, null, displayValueOrDash(entry.fromDisplayValue)), /*#__PURE__*/React.createElement(Label, null, "To Value"), /*#__PURE__*/React.createElement(Value, null, /*#__PURE__*/React.createElement("span", {
    className: "t-audit-trail__inline-value"
  }, valueDisplay, hasRawValue && onViewData && /*#__PURE__*/React.createElement(EyeIconButton, {
    onClick: () => onViewData(entry.rawValueForModal),
    label: "View Data"
  }))), /*#__PURE__*/React.createElement(Label, null, "Location"), /*#__PURE__*/React.createElement(Value, null, hasGeo ? /*#__PURE__*/React.createElement("span", {
    className: "t-audit-trail__geo"
  }, /*#__PURE__*/React.createElement("span", null, entry.latitude, ", ", entry.longitude), /*#__PURE__*/React.createElement("a", {
    href: googleMapsUrl(entry.latitude, entry.longitude),
    target: "_blank",
    rel: "noopener noreferrer",
    className: "t-audit-trail__map-link"
  }, "View in Google Maps")) : '—'), /*#__PURE__*/React.createElement(Label, null, "Entry ID"), /*#__PURE__*/React.createElement(Value, {
    className: "t-audit-trail__detail-entry-id"
  }, entry.id)));
};
Detail.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string,
    breadcrumbs: PropTypes.string,
    contentPath: PropTypes.string,
    created: PropTypes.instanceOf(Date),
    serverTimestamp: PropTypes.instanceOf(Date),
    fromDisplayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rawValueForModal: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
    latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  onViewData: PropTypes.func
};
const Row = _ref8 => {
  let entry = _ref8.entry,
    expanded = _ref8.expanded,
    onToggle = _ref8.onToggle,
    _ref8$onViewData = _ref8.onViewData,
    onViewData = _ref8$onViewData === void 0 ? null : _ref8$onViewData;
  const hasValue = Boolean(entry.displayValue != null && String(entry.displayValue).trim());
  const timeLabel = entry.created ? formatTime(entry.created) : '—';
  const rowClassName = "t-audit-trail__row".concat(expanded ? ' t-audit-trail__row--expanded' : '');
  const valueClassName = "t-audit-trail__row-value".concat(hasValue ? '' : ' t-audit-trail__row-value--empty');
  const detailId = "audit-trail-detail-".concat(entry.id);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    role: "button",
    tabIndex: 0,
    "aria-expanded": expanded,
    "aria-controls": expanded ? detailId : undefined,
    onClick: onToggle,
    onKeyDown: ev => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        onToggle();
      }
    },
    className: rowClassName
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__row-time"
  }, timeLabel), /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__row-main"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__row-text"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__row-field"
  }, entry.field), /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__row-breadcrumbs"
  }, entry.breadcrumbs))), /*#__PURE__*/React.createElement("div", {
    className: valueClassName
  }, hasValue ? entry.displayValue : '—'), /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__row-user"
  }, entry.user), /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__row-toggle"
  }, expanded ? '▾' : '▸')), expanded ? /*#__PURE__*/React.createElement("div", {
    id: detailId
  }, /*#__PURE__*/React.createElement(Detail, {
    entry: entry,
    onViewData: onViewData
  })) : null);
};
Row.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string,
    user: PropTypes.string,
    field: PropTypes.string,
    breadcrumbs: PropTypes.string,
    displayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rawValueForModal: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
    created: PropTypes.instanceOf(Date)
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onViewData: PropTypes.func
};

/**
 * Presentational audit trail viewer.
 * Accepts full form content blob; audit entries are read from auditTrailFormContent.auditTrail.
 * @param {Object} props
 * @param {Object} [props.auditTrailFormContent={}] - Form content blob.
 */
const AuditTrailViewer = _ref9 => {
  let _ref9$auditTrailFormC = _ref9.auditTrailFormContent,
    auditTrailFormContent = _ref9$auditTrailFormC === void 0 ? {} : _ref9$auditTrailFormC;
  const entries = useMemo(() => {
    var _auditTrailFormConten;
    return normalizeAuditTrailEntries((_auditTrailFormConten = auditTrailFormContent === null || auditTrailFormContent === void 0 ? void 0 : auditTrailFormContent.auditTrail) !== null && _auditTrailFormConten !== void 0 ? _auditTrailFormConten : {});
  }, [auditTrailFormContent]);
  const users = useMemo(() => getUniqueSortedValues(entries, 'user'), [entries]);
  const fields = useMemo(() => getUniqueSortedValues(entries, 'breadcrumbs'), [entries]);
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    userFilter = _useState4[0],
    setUserFilter = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    fieldFilter = _useState6[0],
    setFieldFilter = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    datePreset = _useState8[0],
    setDatePreset = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    expandedId = _useState0[0],
    setExpandedId = _useState0[1];
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    viewDataJson = _useState10[0],
    setViewDataJson = _useState10[1];
  const handleViewData = raw => {
    setViewDataJson(stringifyRawValueForModal(raw));
  };
  const filtered = useMemo(() => filterAuditTrailEntries(entries, {
    query,
    userFilter,
    fieldFilter,
    datePreset
  }), [entries, userFilter, fieldFilter, datePreset, query]);
  const groupedByDay = useMemo(() => groupEntriesByDay(filtered), [filtered]);
  const fieldPickerOptions = useMemo(() => fields.map(f => ({
    id: f,
    title: f
  })), [fields]);
  const userPickerOptions = useMemo(() => users.map(u => ({
    id: u,
    title: u
  })), [users]);

  // Menus portaled to body (.t-audit-trail uses overflow:hidden).
  const selectMenuPortalTarget = useMemo(() =>
  // eslint-disable-next-line no-undef
  typeof window === 'undefined' ? null : window.document.body, []);
  const renderEntryRows = list => list.map(e => /*#__PURE__*/React.createElement(Row, {
    key: e.id,
    entry: e,
    expanded: expandedId === e.id,
    onToggle: () => setExpandedId(cur => cur === e.id ? null : e.id),
    onViewData: handleViewData
  }));
  const filteredCount = filtered.length;
  const totalEntryCount = entries.length;
  const showFilteredOfTotal = totalEntryCount > 0 && filteredCount !== totalEntryCount;
  const logNoun = showFilteredOfTotal || filteredCount !== 1 ? 'logs' : 'log';
  let entriesContent;
  if (filtered.length === 0) {
    entriesContent = /*#__PURE__*/React.createElement("div", {
      className: "t-audit-trail__empty"
    }, "No audit entries match your filters.");
  } else {
    entriesContent = groupedByDay.map(_ref0 => {
      let _ref1 = _slicedToArray(_ref0, 2),
        day = _ref1[0],
        list = _ref1[1];
      return /*#__PURE__*/React.createElement(Group, {
        key: day,
        title: day
      }, renderEntryRows(list));
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__filters"
  }, /*#__PURE__*/React.createElement(Filter, {
    label: "Search",
    filterAriaLabel: "Search audit entries"
  }, /*#__PURE__*/React.createElement(InputGroup, {
    className: "t-audit-trail__search-group"
  }, /*#__PURE__*/React.createElement(Input, {
    type: "text",
    value: query,
    onChange: e => setQuery(e.target.value),
    placeholder: "Start typing to search...",
    className: "tqc-control",
    "aria-label": "Search audit entries"
  }), /*#__PURE__*/React.createElement(InputGroupAddon, {
    addonType: "append"
  }, /*#__PURE__*/React.createElement(InputGroupText, {
    className: "t-audit-trail__input-addon"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "search"
  }))))), /*#__PURE__*/React.createElement(Filter, {
    label: "Field",
    filterAriaLabel: "Filter by field"
  }, /*#__PURE__*/React.createElement(SinglePicker, {
    verticalPadding: false,
    returnValueKey: "id",
    valueKey: "id",
    labelKey: "title",
    value: fieldFilter,
    onChange: v => setFieldFilter(v || ''),
    options: fieldPickerOptions,
    placeholder: "Select...",
    searchable: true,
    clearable: true,
    className: "t-audit-trail__picker",
    menuPortalTarget: selectMenuPortalTarget
  })), /*#__PURE__*/React.createElement(Filter, {
    label: "User",
    filterAriaLabel: "Filter by user"
  }, /*#__PURE__*/React.createElement(SinglePicker, {
    verticalPadding: false,
    returnValueKey: "id",
    valueKey: "id",
    labelKey: "title",
    value: userFilter,
    onChange: v => setUserFilter(v || ''),
    options: userPickerOptions,
    placeholder: "Select...",
    searchable: true,
    clearable: true,
    className: "t-audit-trail__picker",
    menuPortalTarget: selectMenuPortalTarget
  })), /*#__PURE__*/React.createElement(Filter, {
    label: "Date",
    filterAriaLabel: "Filter by date"
  }, /*#__PURE__*/React.createElement(SinglePicker, {
    verticalPadding: false,
    returnValueKey: "id",
    valueKey: "id",
    labelKey: "title",
    value: datePreset,
    onChange: v => setDatePreset(v || ''),
    options: DATE_PRESET_OPTIONS,
    placeholder: "Any time",
    searchable: false,
    clearable: true,
    className: "t-audit-trail__picker",
    menuPortalTarget: selectMenuPortalTarget
  }))), /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__body"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__body-header"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__count",
    "data-testid": "audit-trail-count-summary"
  }, "Showing", ' ', /*#__PURE__*/React.createElement("span", {
    className: "t-audit-trail__count-number"
  }, filteredCount), showFilteredOfTotal ? /*#__PURE__*/React.createElement(React.Fragment, null, ' ', "of", ' ', /*#__PURE__*/React.createElement("span", {
    className: "t-audit-trail__count-number"
  }, totalEntryCount)) : null, ' ', logNoun)), /*#__PURE__*/React.createElement("div", {
    className: "t-audit-trail__list"
  }, entriesContent)), viewDataJson != null && /*#__PURE__*/React.createElement(ViewDataModal, {
    jsonString: viewDataJson,
    onClose: () => setViewDataJson(null)
  }));
};
AuditTrailViewer.propTypes = {
  auditTrailFormContent: PropTypes.shape({
    auditTrail: PropTypes.shape({})
  })
};
export default AuditTrailViewer;